// PageFrame — the one gutter owner (root) and the one width-mode owner (nested).
// Page layout system, slice L1a (docs/plans/2026-09-14-page-layout-system-plan.md
// §1.2, §4.1, Complexity Receipt R1).
//
// Two layers, two components:
//   PageFrameRoot — rendered ONCE by the app shell around the page's main
//     content region. Owns the outer gutter (`--page-gutter`) and the
//     trailing bottom clearance spacer that replaces the old FeedbackWidget
//     bottom-reserve hack (R4/R6).
//   PageFrame (nested) — rendered by a page archetype or SettingsPanel INSIDE
//     a root frame. Owns exactly one width-mode decision ('fluid' | 'spine' | 'narrow')
//     for that page composition. No padding of its own -- the root already
//     paid for the gutter.
//
// `usePageFrame()` is the seam consumers (archetypes, SettingsPanel) branch
// on: outside any PageFrameRoot it returns the inert default so every
// existing route (not yet rendered under the shell's lab geometry switch)
// falls through to its legacy markup unchanged.
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

/**
 * `'fluid'` 1920px (lists, reports, workbenches) -- `'spine'` 1440px (detail,
 * dashboard, forms) -- `'narrow'` 896px, the reading width for financial
 * statements (Trial Balance, P&L, Cash Activity...), which used
 * `PageContainer width="narrow"` before they moved onto PageFrame for the
 * locked header (ADR-0188). Reuses the existing `--content-max-width-narrow`
 * token rather than adding a frame token.
 */
export type PageFrameMode = 'fluid' | 'spine' | 'narrow';

export interface PageFrameProps {
  mode: PageFrameMode;
  /**
   * 'page' (default) -- the frame grows with its content; the ancestor
   * main-content region scrolls the whole page. 'contained' -- the frame becomes a
   * bounded flex column (`h-full flex-1 min-h-0`) so a full-height workspace
   * (e.g. `WorkbenchPage fillViewport`) or a locked-header list can scroll its
   * own panels internally instead of the page growing.
   *
   * Both `h-full` AND `flex-1`, deliberately. Under a flex-column parent,
   * `flex: 1 1 0%` wins over `height` on the main axis, so the frame fills only
   * what siblings leave -- a lane hub renders its tab nav beside the frame, and
   * `h-full` alone would overflow that parent by the nav's height and put the
   * shell's main region back to scrolling. Under a plain block parent (the full-bleed catalog item
   * editor, #6216), `flex-1` is inert and `h-full` still bounds it. Inside the
   * shell, the definite height comes from `PageFrameRoot`, which bounds itself
   * via `:has()` on the `data-frame-scroll` stamp below
   * (docs/plans/2026-09-23-locked-list-header-plan.md slice 1).
   */
  scroll?: 'page' | 'contained';
  /** Rendered before `children`, inside the same width-capped content box. */
  header?: ReactNode;
  children: ReactNode;
  /** Non-geometry classes only -- width/padding/container are this component's job. */
  className?: string;
  'data-testid'?: string;
}

interface PageFrameContextValue {
  /** True anywhere inside a `PageFrameRoot`, however many nested frames deep. */
  inRootFrame: boolean;
  /** The nearest enclosing nested `PageFrame`'s mode, or `null` at the root. */
  mode: PageFrameMode | null;
  scroll: 'page' | 'contained';
}

const DEFAULT_CONTEXT: PageFrameContextValue = {
  inRootFrame: false,
  mode: null,
  scroll: 'page',
};

const PageFrameContext = createContext<PageFrameContextValue | null>(null);

/**
 * Outside any `PageFrameRoot`/`PageFrame` this returns the inert default --
 * the seam every archetype branches on to keep rendering its legacy markup
 * on every route that doesn't (yet) mount under the shell's root frame.
 */
export function usePageFrame(): PageFrameContextValue {
  return useContext(PageFrameContext) ?? DEFAULT_CONTEXT;
}

/**
 * Root frame -- rendered once by the app shell around the page's main
 * content region. Owns the outer gutter and the trailing clearance spacer.
 * Shell-only: never rendered by a feature page directly.
 */
export function PageFrameRoot({
  children,
  widgetClearance,
}: {
  children: ReactNode;
  widgetClearance: boolean;
}) {
  const contextValue: PageFrameContextValue = {
    inRootFrame: true,
    mode: null,
    scroll: 'page',
  };
  return (
    <PageFrameContext.Provider value={contextValue}>
      {/* Bounds itself to the shell's main region (a stretched flex item with a definite height)
          ONLY when a descendant frame opted into contained scroll -- pure CSS,
          so page-scroll routes never match and render exactly as before. The
          root renders above the page, so context can't carry this upward
          without a registration effect + re-render. */}
      <div
        data-page-frame="root"
        className="[container-type:inline-size] [container-name:frame] px-[var(--page-gutter)] pt-[var(--page-gutter)] has-[[data-frame-scroll=contained]]:flex has-[[data-frame-scroll=contained]]:h-full has-[[data-frame-scroll=contained]]:flex-col"
      >
        {children}
        {/* Bottom clearance -- replaces the old FeedbackWidget bottom-reserve
            compensator (R4). `--widget-clearance` collapses to 0 once a BottomDock is
            mounted or the widget has moved off its default position, so the
            spacer never doubles up with the dock's own reserved height. */}
        <div
          aria-hidden="true"
          data-page-frame-clearance=""
          className={
            widgetClearance
              ? 'h-[calc(var(--page-gutter)_+_var(--widget-clearance))] shrink-0'
              : 'h-[var(--page-gutter)] shrink-0'
          }
        />
      </div>
    </PageFrameContext.Provider>
  );
}

const MODE_MAX_WIDTH: Record<PageFrameMode, string> = {
  fluid: 'max-w-[var(--frame-max-fluid)]',
  spine: 'max-w-[var(--frame-max-spine)]',
  narrow: 'max-w-[var(--content-max-width-narrow)]',
};

/**
 * Nested frame -- rendered by a page archetype (or `SettingsPanel`) INSIDE a
 * root frame. Owns the one width-mode decision for that page composition.
 * A `PageFrame` rendered inside another `PageFrame`'s subtree re-decides the
 * composition width twice; dev-only warning, per DrawerHeader's existing
 * `process.env['NODE_ENV']` idiom in this package.
 */
export function PageFrame({
  mode,
  scroll = 'page',
  header,
  children,
  className,
  'data-testid': dataTestId,
}: PageFrameProps) {
  const parent = useContext(PageFrameContext);
  // A parent whose own `mode` is already set means the parent is itself a
  // nested PageFrame (only PageFrameRoot's context leaves `mode: null`).
  const isNestedInNested = parent?.mode != null;
  const warnedRef = useRef(false);
  useEffect(() => {
    if (process.env['NODE_ENV'] !== 'production' && isNestedInNested && !warnedRef.current) {
      warnedRef.current = true;
      console.warn(
        '[PageFrame] rendered inside another PageFrame -- a page composition owns exactly one width-mode decision. Remove the inner PageFrame or hoist its content into the outer one.',
      );
    }
  }, [isNestedInNested]);

  const contextValue: PageFrameContextValue = {
    inRootFrame: parent?.inRootFrame ?? false,
    mode,
    scroll,
  };

  return (
    <PageFrameContext.Provider value={contextValue}>
      <div
        data-page-frame="nested"
        data-frame-scroll={scroll}
        data-testid={dataTestId}
        className={[
          'mx-auto w-full [container-type:inline-size] [container-name:frame]',
          MODE_MAX_WIDTH[mode],
          scroll === 'contained' ? 'flex h-full min-h-0 flex-1 flex-col' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {header}
        {children}
      </div>
    </PageFrameContext.Provider>
  );
}
